import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Breadcrumb } from '@shared/components/bootstrap';

import { PageHeader } from '@admin/components/helpers/page_header';
import { useTitle } from '@admin/hooks/use_title';

import { errorMessageFromResponse, fetchMoverUsers, fetchUnavailabilities } from './api';
import { dailyResultsForUsers } from './availability_logic';
import { DailyView } from './daily_view';
import { FiltersPanel } from './filters_panel';
import { cleanupFilters } from './filters_util';
import { isDailyFiltering, isManagementFiltering, parseFiltersFromSearch, syncFiltersToUrl } from './filters_util';
import { ManagementView } from './management_view';
import { IAvailabilityFilters, IAvailabilityUser, IUnavailability } from './types';

export const AvailabilityView: React.FC = () => {
  useTitle('Availability');
  const [filters, setFilters] = useState<IAvailabilityFilters>(() => parseFiltersFromSearch(window.location.search));
  const [users, setUsers] = useState<IAvailabilityUser[]>([]);
  const [unavailabilities, setUnavailabilities] = useState<IUnavailability[] | null>(null);
  const [dailyResults, setDailyResults] = useState<Record<number, IUnavailability[]> | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const requestIdRef = useRef(0);

  useEffect(() => {
    fetchMoverUsers()
      .then(setUsers)
      .catch((error) => setErrorMessage(errorMessageFromResponse(error)));
  }, []);

  const loadUnavailabilities = useCallback(async (nextFilters: IAvailabilityFilters) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (!isDailyFiltering(nextFilters) && !isManagementFiltering(nextFilters)) {
      setUnavailabilities(null);
      setDailyResults(null);
      return;
    }

    setLoading(true);
    setUnavailabilities(null);
    setDailyResults(null);

    try {
      const params = cleanupFilters(
        isDailyFiltering(nextFilters)
          ? { region_id: nextFilters.region_id, date: nextFilters.date }
          : { user_id: nextFilters.user_id },
      );

      const data = await fetchUnavailabilities(params);
      if (requestId !== requestIdRef.current) {
        return;
      }

      setUnavailabilities(data);
    } catch (error) {
      if (requestId === requestIdRef.current) {
        setErrorMessage(errorMessageFromResponse(error));
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    syncFiltersToUrl(filters);
    loadUnavailabilities(filters);
  }, [filters, loadUnavailabilities]);

  useEffect(() => {
    if (!isDailyFiltering(filters) || !unavailabilities) {
      return;
    }
    setDailyResults(dailyResultsForUsers({ date: filters.date }, users, unavailabilities));
  }, [filters, unavailabilities, users]);

  const onFiltersChange = (nextFilters: IAvailabilityFilters) => {
    setErrorMessage(undefined);
    setFilters(nextFilters);
  };

  return (
    <>
      <PageHeader>
        <Breadcrumb>
          <Breadcrumb.Item active>Availability</Breadcrumb.Item>
        </Breadcrumb>
      </PageHeader>

      <FiltersPanel filters={filters} users={users} onChange={onFiltersChange} />

      {filters.mode === 'daily' && isDailyFiltering(filters) && (
        <DailyView users={users} results={dailyResults} regionID={filters.region_id} />
      )}

      {filters.mode === 'management' && isManagementFiltering(filters) && (
        <ManagementView
          filters={filters}
          unavailabilities={unavailabilities}
          loading={loading}
          errorMessage={errorMessage}
          onReload={() => loadUnavailabilities(filters)}
          onClearError={() => setErrorMessage(undefined)}
          onError={setErrorMessage}
        />
      )}
    </>
  );
};
